import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import {
  EditOutlined,
  MoreVertOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { getClaimFormIcon } from "../../utils/claimFormIcons.js";
import { useAbility } from "../../hooks/permissions/useAbility.jsx";
import ClaimFormPreviewModal from "../../pages/admin/claim-forms/components/ClaimFormPreviewModal.jsx";
import ClaimFormDefinitionEditModal from "../../pages/admin/claim-forms/components/ClaimFormDefinitionEditModal.jsx";

export default function ClaimFormsTable() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { can } = useAbility();
  const [previewClaimForm, setPreviewClaimForm] = useState(null);
  const [editingClaimForm, setEditingClaimForm] = useState(null);
  const [rowMenuState, setRowMenuState] = useState({
    anchorEl: null,
    menuRowId: null,
  });

  const handleMenuOpen = (event, id) => {
    event.stopPropagation();
    setRowMenuState({ anchorEl: event.currentTarget, menuRowId: id });
  };
  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  };

  const tableKey = "claim_forms";
  const storageKey = "v2_table_claim_forms";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Schadenformulare",
        route: "data/claim-forms",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          sort: [{ field: "label", order: "asc" }],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: "label",
      header: "Name",
      size: 250,
    },
    {
      accessorKey: "icon_key",
      header: "Icon",
      enableSorting: false,
      size: 120,
      Cell: ({ cell }) => {
        const iconKey = cell.getValue();
        if (!iconKey) {
          return "–";
        }
        const Icon = getClaimFormIcon(iconKey);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon fontSize="small" />
            <Typography variant="body2">{iconKey}</Typography>
          </Box>
        );
      },
    },
    {
      accessorFn: (row) => row.current_definition?.docx_template ?? "–",
      accessorKey: "docx_template",
      header: "DOCX-Vorlage",
      enableSorting: false,
      size: 200,
    },
    {
      accessorKey: "sparten_count",
      header: "Sparten",
      enableSorting: false,
      size: 100,
    },
    {
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      enableColumnFilter: false,
      enableColumnHiding: false,
      size: 60,
      Cell: ({ row }) => (
        <>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.id)}>
            <MoreVertOutlined fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={rowMenuState.anchorEl}
            open={
              Boolean(rowMenuState.anchorEl) &&
              rowMenuState.menuRowId === row.id
            }
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={(e) => {
                handleMenuClose(e);
                setPreviewClaimForm(row.original);
              }}
            >
              <VisibilityOutlined fontSize="small" sx={{ mr: 1 }} />
              Vorschau
            </MenuItem>
            {can("ClaimForm", "update") && (
              <MenuItem
                onClick={(e) => {
                  handleMenuClose(e);
                  setEditingClaimForm(row.original);
                }}
              >
                <EditOutlined fontSize="small" sx={{ mr: 1 }} />
                Definition bearbeiten
              </MenuItem>
            )}
          </Menu>
        </>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        initialState={{ showGlobalFilter: true }}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? defaultRowsPerPage,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        onChangeGlobalFilter={(searchword) =>
          updateTableState(tableKey, { searchword })
        }
        onRowClick={(row) => navigate(`/admin/claim-forms/${row.id}`)}
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={false}
        enableColumnHiding={true}
      />
      <ClaimFormPreviewModal
        open={!!previewClaimForm}
        onClose={() => setPreviewClaimForm(null)}
        claimForm={previewClaimForm}
      />
      <ClaimFormDefinitionEditModal
        open={!!editingClaimForm}
        onClose={() => setEditingClaimForm(null)}
        claimForm={editingClaimForm}
        onSaved={() => fetchData(tableKey)}
      />
    </>
  );
}
